import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, CreditCard, MapPin, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MarkdownText from '../components/MarkdownText';
import { eventsAPI, locationsAPI } from '../utils/api';
import { toast } from 'sonner';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const ev = await eventsAPI.getById(id);
        setEvent(ev.data);
        const loc = await locationsAPI.getById(ev.data.location_id);
        setLocation(loc.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Akce nenalezena');
        navigate('/locations');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-40 text-center text-gray-600">Načítám…</div>
      </div>
    );
  }
  if (!event) return null;

  const ownerName = location?.owner?.name;
  const ownerFirstName = ownerName ? String(ownerName).trim().split(/\s+/)[0] : null;
  const mailHref = location?.owner?.email
    ? `mailto:${location.owner.email}?subject=${encodeURIComponent(`Dotaz k akci: ${event.name}`)}`
    : null;

  return (
    <div className="min-h-screen">
      <div
        className="relative bg-cover bg-center py-12"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url('${location?.photos?.[0] || 'https://customer-assets.emergentagent.com/job_elementor-site-1/artifacts/ff4gwxo8_ChatGPT%20Image%2031.%203.%202026%2023_45_22.png'}')`,
        }}
      >
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-2 text-white">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10 mb-3 -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Zpět
          </Button>
          <Badge className="bg-orange-600 text-white mb-3">ORGANIZOVANÁ AKCE</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">{event.name}</h1>
          {location && (
            <p className="text-white/90 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <Link to={`/location/${location.id}`} className="underline">{location.name}</Link>
            </p>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {event.photos && event.photos.length > 0 && (
              <Card>
                <CardContent className="p-3">
                  <div className={`grid gap-3 ${event.photos.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`} data-testid="event-photos">
                    {event.photos.map((src, idx) => (
                      <img
                        key={idx}
                        src={src}
                        alt={`${event.name} foto ${idx + 1}`}
                        className="w-full h-56 sm:h-72 object-cover rounded-lg"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Detaily akce</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <Info icon={<Calendar className="w-4 h-4" />} label="Začátek" value={event.start_date} />
                  <Info icon={<Calendar className="w-4 h-4" />} label="Konec" value={event.end_date} />
                  <Info icon={<Users className="w-4 h-4" />} label="Max. osob" value={event.max_persons} />
                  <Info
                    icon={<CreditCard className="w-4 h-4" />}
                    label="Cena"
                    value={`${event.price_per_person} Kč / osoba`}
                  />
                </div>
                {event.description && (
                  <p className="mt-5 text-gray-700"><MarkdownText text={event.description} /></p>
                )}
              </CardContent>
            </Card>

            {event.details && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-3">Více informací</h2>
                  <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    <MarkdownText text={event.details} />
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <div>
            <Card className="shadow-lg">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-bold text-lg">Mám zájem</h3>
                <p className="text-sm text-gray-600">
                  Pro účast nebo dotazy kontaktujte přímo organizátora akce.
                </p>
                {ownerFirstName && (
                  <p className="text-sm">
                    Organizuje: <strong>{ownerFirstName}</strong>
                  </p>
                )}
                {mailHref && (
                  <Button asChild className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                    <a href={mailHref}>
                      <Mail className="w-4 h-4 mr-2" /> Kontaktovat organizátora
                    </a>
                  </Button>
                )}
                <p className="text-xs text-gray-500">
                  WildCamping nezprostředkovává platby ani rezervace pro organizované akce. Vše domlouvejte přímo s organizátorem.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const Info = ({ icon, label, value }) => (
  <div>
    <p className="text-gray-500 flex items-center gap-1 mb-1">
      {icon} {label}
    </p>
    <p className="font-semibold">{value}</p>
  </div>
);

export default EventDetail;
